import React, {useState} from 'react'
import {FiAlertTriangle, FiTrash2} from 'react-icons/fi'
import PrimaryButton from '../common/buttons/PrimaryButton'

interface DeleteConfirmationModalProps {
  title?: string
  heading?: string
  description?: string
  show: boolean
  onConfirm: () => void | Promise<void>
  onCancel: () => void
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  title = 'Confirm Deletion',
  heading = 'Are you absolutely sure?',
  description = 'This action cannot be undone. This will permanently delete the item and remove all associated data.',
  show,
  onConfirm,
  onCancel,
}) => {
  const [isDeleting, setIsDeleting] = useState(false)

  const handleConfirm = async () => {
    if (isDeleting) return // Prevent multiple clicks
    
    setIsDeleting(true)
    try {
      await onConfirm()
    } catch (error) {
      console.error('Error during deletion:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  if (!show) return null

  return (
    <div
      className='modal fade show'
      style={{
        display: 'block',
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(4px)',
      }}
      aria-modal='true'
    >
      <div className='modal-dialog modal-dialog-centered'>
        <div
          className='modal-content border-0 shadow-lg'
          style={{borderRadius: '12px', overflow: 'hidden'}}
        >
          {/* Modal Header */}
          <div className='modal-header bg-danger-50 border-0' style={{padding: '1.5rem 2rem'}}>
            <h5 className='modal-title d-flex align-items-center gap-2'>
              <FiAlertTriangle className='text-danger' size={24} />
              <span className='text-danger fw-semibold'>{title}</span>
            </h5>
            <button
              type='button'
              className='btn-close'
              aria-label='Close'
              onClick={onCancel}
              style={{fontSize: '0.8rem'}}
            />
          </div>

          {/* Modal Body */}
          <div className='modal-body text-center py-4 px-3' style={{padding: '2rem'}}>
            <div className='d-flex justify-content-center mb-3'>
              <div
                className='bg-danger-10 rounded-circle d-flex align-items-center justify-content-center'
                style={{
                  width: '80px',
                  height: '80px',
                  background: 'rgba(220, 53, 69, 0.1)',
                }}
              >
                <FiTrash2 size={32} className='text-danger' style={{strokeWidth: '1.5px'}} />
              </div>
            </div>

            <h5 className='fw-semibold mb-2'>{heading}</h5>
            <p
              className='text-muted mb-0'
              style={{lineHeight: 1.5, maxWidth: '320px', margin: '0 auto'}}
            >
              {description}
            </p>
          </div>

          {/* Modal Footer */}
          <div
            className='modal-footer border-0'
          >
            <PrimaryButton
            color='secondary d-flex align-items-center justify-content-center gap-2'
            onClick={onCancel}
            disabled={isDeleting}
            >
              Cancel
            </PrimaryButton>
            <PrimaryButton
            color='danger'
            onClick={handleConfirm}
            disabled={isDeleting}
            className='d-flex align-items-center justify-content-center gap-2'
            >
              {!isDeleting ? (
                <>
                  <FiTrash2 size={16} />
                  Confirm Delete
                </>
              ) : (
                <>
                  <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />{' '}
                  Deleting...
                </>
              )}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeleteConfirmationModal
